const STORAGE_KEY = 'kandyQuizAnswers';
const QUIZ_URL = '/pages/longevity-plan-quiz';
const RESULTS_URL = '/pages/longevity-plan-quiz-results';
// Same ?qa= payload as the results page, but the page there adds to cart on load and redirects
// to checkout instead of rendering the plan. Built for Klaviyo emails — see buildCheckoutLink.
const CHECKOUT_URL = '/pages/longevity-plan-checkout';

// null = required (missing → retake quiz). Any other value = default to use.
const QUIZ_FIELD_DEFAULTS = {
  name:     null,
  gender:   null,
  weight:   null,
  email:    null,
  neutered: 'no',
  allergies: [],
  age:      'adult',
  activity: 'moderate',
  appetite: 'depends',
  goal:     'wellbeing',
  foodType: 'fresh',
  currentRecipe: '',
};

function backfillAnswers(decoded) {
  const result = { ...decoded, isCompleted: true };
  for (const [key, defaultValue] of Object.entries(QUIZ_FIELD_DEFAULTS)) {
    const val = result[key];
    if (val === undefined || val === null || val === '') {
      if (defaultValue === null) return null;
      result[key] = defaultValue;
    }
  }
  return result;
}
const STOREFRONT_API_VERSION = '2026-01';
const LOOP_SELLING_PLAN = 698239648068;
const KIBBLE_SELLING_PLAN = 692934639940;
const DEFAULT_DISCOUNT_CODE = 'LONGEVITY20';
const DEFAULT_DISCOUNT_PERCENT = 20;
// Kibble gets this automatically, for life — every kibble order applies it, campaign codes aside.
const KIBBLE_LIFETIME_DISCOUNT_CODE = 'LONGEVITY-10';
const KIBBLE_LIFETIME_DISCOUNT_PERCENT = 10;

// Campaign codes outside the LONGEVITY<n> family. Every other code in this flow encodes its own
// percent in its name (LONGEVITY20 -> 20%); these can't, so the percent is declared here instead.
// Keeping it an explicit allowlist means an arbitrary code can never grant a discount by accident.
//   key   = ASCII-folded, uppercased form used for matching (so an accent typed either way resolves)
//   code  = the string sent to Shopify — it MUST match the discount code in Admin character for
//           character, or the page shows -25% while checkout charges the undiscounted price.
const CAMPAIGN_DISCOUNTS = {
  FIMDOMES25: { code: 'FIMDOMÊS25', percent: 25 },
  PROMO25:    { code: 'PROMO25',    percent: 25 },
};

// Strip diacritics so "FIMDOMÊS25" and "FIMDOMES25" both hit the same registry entry.
function normaliseCode(code) {
  return (code || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
}

function campaignDiscount(code) {
  return CAMPAIGN_DISCOUNTS[normaliseCode(code)] || null;
}

const TOPPING_VARIANTS = [
  { id: 56790057943364, quantity: 1 },
];

const FRESH_VARIANT_MAP = [
  { maxKg: 1,        trialId: 56790056763716, ongoingId: 56790057353540 },
  { maxKg: 3,        trialId: 56790056796484, ongoingId: 56790057386308 },
  { maxKg: 5,        trialId: 56790056829252, ongoingId: 56790057419076 },
  { maxKg: 7,        trialId: 56790056862020, ongoingId: 56790057451844 },
  { maxKg: 9,        trialId: 56790056894788, ongoingId: 56790057484612 },
  { maxKg: 13,       trialId: 56790056927556, ongoingId: 56790057517380 },
  { maxKg: 21,       trialId: 56790056960324, ongoingId: 56790057550148 },
  { maxKg: 25,       trialId: 56790056993092, ongoingId: 56790057582916 },
  { maxKg: 31,       trialId: 56790057025860, ongoingId: 56790057615684 },
  { maxKg: 37,       trialId: 56790057058628, ongoingId: 56790057648452 },
  { maxKg: 44,       trialId: 56790057091396, ongoingId: 56790057681220 },
  { maxKg: 50,       trialId: 56790057124164, ongoingId: 56790057713988 },
  { maxKg: 58,       trialId: 56790057156932, ongoingId: 56790057746756 },
  { maxKg: 65,       trialId: 56790057189700, ongoingId: 56790057779524 },
  { maxKg: 72,       trialId: 56790057222468, ongoingId: 56790057812292 },
  { maxKg: 80,       trialId: 56790057255236, ongoingId: 56790057845060 },
  { maxKg: 88,       trialId: 56790057288004, ongoingId: 56790057877828 },
  { maxKg: Infinity, trialId: 56790057320772, ongoingId: 56790057910596 },
];

// Matches both the campaign-style "LONGEVITY20" and the lifetime "LONGEVITY-10" formats.
function isLongevityCode(code) {
  return /^LONGEVITY-?\d+$/.test((code || '').toUpperCase());
}

// Every code this flow will honour: the LONGEVITY family plus the declared campaign codes.
function isKnownDiscountCode(code) {
  return isLongevityCode(code) || !!campaignDiscount(code);
}

// The hyphenated form (LONGEVITY-10) is reserved for kibble's automatic lifetime discount —
// it must never be picked up as a candidate for a fresh session (e.g. a stale code left on
// the real Shopify cart from a previous kibble visit).
function isKibbleOnlyCode(code) {
  return /^LONGEVITY-\d+$/.test((code || '').toUpperCase());
}

function parseDiscountPercent(code) {
  const campaign = campaignDiscount(code);
  if (campaign) return campaign.percent;
  const match = /^LONGEVITY-?(\d+)$/.exec((code || '').toUpperCase());
  return match ? parseInt(match[1], 10) : 0;
}

// The exact string to hand Shopify. Campaign codes are stored under a folded key, so resolve
// back to the canonical spelling rather than echoing whatever the URL happened to carry.
function canonicalCode(code) {
  const campaign = campaignDiscount(code);
  return campaign ? campaign.code : (code || '').toUpperCase();
}

function setDiscountIfBetter(code) {
  const incoming = code.toUpperCase();
  const existing = localStorage.getItem('longevityDiscountCode') || '';
  if (parseDiscountPercent(incoming) >= parseDiscountPercent(existing)) {
    localStorage.setItem('longevityDiscountCode', incoming);
  }
}

function getActiveDiscount(cartCode, answers, isKibble) {
  // Kibble's 10% is automatic and floor-only. It can only be raised by an explicit signal for
  // THIS session — a `?discount=` URL param, or an answer set directly on this quiz completion —
  // never by the shared cart/localStorage discount code, which can carry a stale code left over
  // from an unrelated fresh session (the real Shopify cart persists across quiz retakes).
  const urlDiscountCode = (new URLSearchParams(window.location.search).get('discount') || '').toUpperCase();
  const candidates = (isKibble
    ? [urlDiscountCode, (answers && answers.discountCode) || '']
    : [cartCode, localStorage.getItem('longevityDiscountCode'), (answers && answers.discountCode) || '']
  )
    .map(c => (c || '').toUpperCase())
    .filter(c => isKnownDiscountCode(c) && (isKibble || !isKibbleOnlyCode(c)));

  const defaultPercent = isKibble ? KIBBLE_LIFETIME_DISCOUNT_PERCENT : DEFAULT_DISCOUNT_PERCENT;
  const defaultCode = isKibble ? KIBBLE_LIFETIME_DISCOUNT_CODE : DEFAULT_DISCOUNT_CODE;

  if (candidates.length > 0) {
    const best = candidates.reduce((a, b) =>
      parseDiscountPercent(a) >= parseDiscountPercent(b) ? a : b
    );
    const bestPercent = parseDiscountPercent(best);
    // Kibble's lifetime discount is a floor — a bigger campaign code wins, a smaller/stale one doesn't.
    if (isKibble && bestPercent < defaultPercent) return { percent: defaultPercent, code: defaultCode };
    return { percent: bestPercent, code: canonicalCode(best) };
  }
  return { percent: defaultPercent, code: defaultCode };
}

function loadAnswers() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const item = JSON.parse(raw);
    if (Date.now() > item.expiry) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return item.value || null;
  } catch (e) {
    return null;
  }
}

function getFreshTier(weightKg) {
  const w = parseFloat(weightKg);
  if (!w || w <= 0) return FRESH_VARIANT_MAP[0];
  return FRESH_VARIANT_MAP.find(t => w <= t.maxKg) || FRESH_VARIANT_MAP[FRESH_VARIANT_MAP.length - 1];
}

function normalizeKibbleAge(age) {
  if (age === 'puppy') return 'Puppy (- 1 ano)';
  if (age === 'senior') return 'Sénior (+7 anos)';
  return 'Adulto (1 a 7 anos)';
}

function resolveKibbleRecipe(answers) {
  // No hypoallergenic variant for puppies — fall back to salmon
  if (answers.currentRecipe === 'hypoallergenic' && answers.age === 'puppy') return 'fish';
  return answers.currentRecipe;
}

function normalizeKibbleProtein(currentRecipe, isMini) {
  if (currentRecipe === 'hypoallergenic') return 'Hipoalergénica';
  const base = currentRecipe === 'fish' ? 'Salmão fresco' : 'Frango & peru fresco';
  return isMini ? `${base} · biscoito mini` : base;
}

function getKibbleBagWeight(weightKg) {
  const w = parseFloat(weightKg);
  if (!w || w <= 0) return '3kg';
  if (w <= 5) return '3kg';
  if (w <= 10) return '6kg';
  if (w <= 20) return '9kg';
  if (w <= 30) return '12kg';
  if (w <= 40) return '18kg';
  return '24kg';
}

// Days of food in one kibble bag — the per-day divisor and the total's suffix both read it.
const KIBBLE_BAG_DAYS = 30;

function getKibbleVariant(answers) {
  const variantMap = window.__lprKibbleVariantMap || {};
  const age = normalizeKibbleAge(answers.age);
  const weight = parseFloat(answers.weight);
  const recipe = resolveKibbleRecipe(answers);
  const isHypo = recipe === 'hypoallergenic';
  const isMini = !isHypo && weight <= 9 && answers.age !== 'puppy';
  const protein = normalizeKibbleProtein(recipe, isMini);
  const bagWeight = getKibbleBagWeight(answers.weight);
  const key = `${age}_${protein}_${bagWeight}`;
  const entry = variantMap[key];
  if (!entry) {
    console.warn('[lpr] kibble variant not found for key:', key, '| available:', Object.keys(variantMap));
    return null;
  }
  return entry;
}

/**
 * Everything the quiz collected, as the `_dogs` line item property.
 *
 * The official fields are the dog's own columns: name, gender, weight,
 * lifeStage, breed, birthdate. Everything else the quiz asks goes in
 * additional_info as a free-form blob, keeping the quiz's own values so nothing
 * is lost in translation.
 *
 * Empty answers are dropped from additional_info — a fresh dog has `allergies`
 * and no `current_recipe`, a kibble dog the other way round.
 *
 * Must stay in sync with buildDogLineItemProperties in conversion-flow.klaviyo.js,
 * which builds the same payload for the other CTA path.
 */
function buildDogPayload(answers) {
  const allergies = (Array.isArray(answers.allergies) ? answers.allergies : [])
    .filter(a => a && a !== 'none');

  const additional = {
    food_type: answers.foodType || '',
    neutered: answers.neutered || '',
    activity: answers.activity || '',
    appetite: answers.appetite || '',
    goal: answers.goal || '',
    current_recipe: answers.currentRecipe || '',
    ...(allergies.length ? { allergies } : {}),
    // Only meaningful for puppies — they're the only ones asked for a birthdate.
    ...(answers.age === 'puppy' && answers.isBirthdateUnknown
      ? { birthdate_unknown: true }
      : {}),
    // WEB-334: which quiz (and which dog of it, always the first in v1) this dog came from, so the
    // purchase links to the lead in barkyn-core.
    ...(answers.submissionId
      ? { quiz_submission_id: answers.submissionId, quiz_dog_index: 0 }
      : {}),
  };

  Object.keys(additional).forEach(key => {
    if (additional[key] === '') delete additional[key];
  });

  return {
    name: answers.name || '',
    gender: answers.gender || '',
    weight: answers.weight || '',
    lifeStage: answers.age || '',
    breed: '',
    // Month and year only — the quiz never asks for a day, so it's always 01.
    birthdate: answers.birthdate || '',
    additional_info: additional,
  };
}

const FRESH_COPY_TEMPLATES = {
  hero_title:   'O pack de Boas-Vindas {do_name}',
  starter_intro: 'Tudo o que {o_name} precisa para começar bem: 2 semanas de comida pensadas para uma transição suave e sem surpresas.',
  ongoing_desc: 'Após o pack de Boas-Vindas, para vossa comodidade, {o_name} receberá a sua alimentação personalizada automaticamente todos os meses à sua porta.',
};

function applyCopy(answers, isKibble) {
  const kibbleCopy = isKibble ? (window.__lprKibbleCopy || {}) : {};
  document.querySelectorAll('[data-lpr-copy]').forEach(el => {
    const key = el.dataset.lprCopy;
    const template = (isKibble && kibbleCopy[key]) || FRESH_COPY_TEMPLATES[key];
    if (template) el.innerHTML = interpolate(template, answers);
  });
  document.querySelectorAll('[data-lpr-name]').forEach(el => {
    el.textContent = answers.name || 'seu cão';
  });
}

function fillKibbleCard(answers) {
  const bagWeight = getKibbleBagWeight(answers.weight);
  const weight = parseFloat(answers.weight);
  const resolvedRecipe = resolveKibbleRecipe(answers);
  const isHypo = resolvedRecipe === 'hypoallergenic';
  const isMini = !isHypo && weight <= 9 && answers.age !== 'puppy';
  const recipe = normalizeKibbleProtein(resolvedRecipe, isMini);
  document.querySelectorAll('[data-lpr-portion]').forEach(el => {
    el.textContent = bagWeight;
  });
  document.querySelectorAll('[data-lpr-recipe]').forEach(el => {
    el.textContent = recipe;
  });
}

// Same treatment as the fresh row: per-day stays the headline number, the full amount charged goes
// underneath, and the per-day figure is derived FROM the total so per-day × days lands back on it.
// For kibble the total is the monthly bag, which is what KIBBLE_BAG_DAYS covers.
function fillKibblePrices(answers) {
  const kibbleEntry = getKibbleVariant(answers);
  if (!kibbleEntry) return;
  const discountPct = window.__lprDiscount ?? 20;
  const fullTotal = kibbleEntry.subscriptionPrice;
  const discTotal = discountPct > 0 ? discounted(fullTotal, discountPct) : null;

  const fullEl = document.querySelector('[data-lpr-price="ongoing-full"]');
  const discEl = document.querySelector('[data-lpr-price="ongoing"]');
  const totalEl = document.querySelector('[data-lpr-total="ongoing"]');

  if (discTotal !== null) {
    if (fullEl) {
      fullEl.textContent = formatMoney(Math.round(fullTotal / KIBBLE_BAG_DAYS));
      fullEl.style.display = '';
    }
    if (discEl) discEl.textContent = formatMoney(Math.round(discTotal / KIBBLE_BAG_DAYS));
    if (totalEl) writeTotal(totalEl, discTotal, KIBBLE_BAG_DAYS);
  } else {
    if (fullEl) fullEl.style.display = 'none';
    if (discEl) discEl.textContent = formatMoney(Math.round(fullTotal / KIBBLE_BAG_DAYS));
    if (totalEl) writeTotal(totalEl, fullTotal, KIBBLE_BAG_DAYS);
  }
}

const INGREDIENT_ICON_MAP = {
  duck: '🦆',
  chicken: '🐔',
  turkey: '🦃',
  fish: '🐟',
  salmon: '🐟',
  potato: '🥔',
  batata: '🥔',
  pea: '🌱',
  ervilha: '🌱',
};

function escapeHtml(str) {
  return (str || '').replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

const DIACRITICS_REGEX = new RegExp('[̀-ͯ]', 'g');

function getIngredientIcon(name) {
  const slug = (name || '').toLowerCase().normalize('NFD').replace(DIACRITICS_REGEX, '');
  const match = Object.keys(INGREDIENT_ICON_MAP).find(key => slug.includes(key));
  return match ? INGREDIENT_ICON_MAP[match] : '🥩';
}

function fillRecipeContent(answers) {
  const wrapper = document.querySelector('[data-lpr-recipe-content]');
  if (!wrapper) return;

  const kibbleEntry = getKibbleVariant(answers);
  const ingredients = kibbleEntry?.naturalIngredients || [];
  const composition = kibbleEntry?.composition || '';
  const nutritionalValues = kibbleEntry?.nutritionalValues || '';
  const additives = kibbleEntry?.additives || '';

  if (!ingredients.length && !composition && !nutritionalValues && !additives) {
    wrapper.hidden = true;
    return;
  }
  wrapper.hidden = false;

  const ingredientsEl = document.querySelector('[data-lpr-ingredients]');
  if (ingredientsEl) {
    ingredientsEl.innerHTML = ingredients.map(entry => {
      const name = entry?.name || '';
      const description = entry?.description || '';
      const iconHtml = entry?.icon
        ? `<img class="lpr-ingredient__icon-img" src="${escapeHtml(entry.icon)}" alt="" width="66" height="66" loading="lazy">`
        : getIngredientIcon(name);
      return `<div class="lpr-ingredient">
        <div class="lpr-ingredient__icon">${iconHtml}</div>
        <div class="lpr-ingredient__body">
          <div class="lpr-ingredient__name">${escapeHtml(name)}</div>
          <div class="lpr-ingredient__desc">${escapeHtml(description)}</div>
        </div>
      </div>`;
    }).join('');
  }

  // The accordion body applies a markdown pass to its initial content that strips custom
  // data-* markers before this script runs, so target the body element itself (by its
  // accordion's own class) instead of a nested placeholder node.
  const compositionEl = document.querySelector('.lpr-accordion--composition .accordion__body');
  if (compositionEl) compositionEl.textContent = composition;

  const nutritionalEl = document.querySelector('.lpr-accordion--nutritional .accordion__body');
  if (nutritionalEl) nutritionalEl.textContent = nutritionalValues;

  const additivesEl = document.querySelector('.lpr-accordion--additives .accordion__body');
  if (additivesEl) additivesEl.textContent = additives;
}

function fillFreshAccordions() {
  const wrapper = document.querySelector('[data-lpr-recipe-content]');
  if (!wrapper) return;

  const content = window.__lprFreshContent || {};
  wrapper.hidden = false;

  const compositionEl = document.querySelector('.lpr-accordion--composition .accordion__body');
  if (compositionEl) compositionEl.textContent = content.composition || '';

  const nutritionalEl = document.querySelector('.lpr-accordion--nutritional .accordion__body');
  if (nutritionalEl) nutritionalEl.textContent = content.nutritionalValues || '';

  const storageEl = document.querySelector('.lpr-accordion--additives .accordion__body');
  if (storageEl) storageEl.textContent = content.storageUse || '';
}

function formatMoney(cents) {
  return '€' + (cents / 100).toFixed(2).replace('.', ',');
}

// Shopify applies a percentage code as an AMOUNT and truncates it to the cent, so the price it
// charges is the next cent up from price × (1 - pct): 27,98 - floor(2,798) = 25,19, where
// Math.round would have shown 25,18. Mirroring that here keeps the page from ever quoting less
// than the checkout takes. Found on ES against a live checkout, 2026-09-09.
function discounted(cents, pct) {
  return cents - Math.floor((cents * pct) / 100);
}

// The amount carries the emphasis; the duration next to it stays quiet, so the line reads as one
// price and not as two competing numbers. Both values are formatted here, never user input.
function writeTotal(el, cents, days) {
  el.innerHTML =
    `${formatMoney(cents)} <span class="lpr-pricing-row__total-note">· ${days} dias</span>`;
}

function calcPortionGrams(weightKg) {
  const w = parseFloat(weightKg);
  if (!w || w <= 0) return 250;
  return Math.round(w * 25);
}

function formatGrams(g) {
  return g >= 1000 ? `${(g / 1000).toFixed(1).replace('.0', '')}kg` : `${g}g`;
}

function interpolate(text, answers) {
  const rawName = answers.name;
  const isFemale = answers.gender === 'female';
  const name = rawName || 'seu cão';
  const oA = isFemale ? 'a' : 'o';
  const OA = isFemale ? 'A' : 'O';
  const doDA = isFemale ? 'da' : 'do';
  const protein = answers.currentRecipe === 'fish' ? 'peixe' : 'carne';
  return text
    .replace(/{O_name}/g, rawName ? `${OA} ${name}` : 'O seu cão')
    .replace(/{o_name}/g, rawName ? `${oA} ${name}` : 'o seu cão')
    .replace(/{do_name}/g, rawName ? `${doDA} ${name}` : 'do seu cão')
    .replace(/{name}/g, name)
    .replace(/{protein}/g, protein);
}


function fillPortion(answers) {
  const grams = calcPortionGrams(answers.weight);
  const toppingGrams = Math.round(grams * 0.4 / 5) * 5;
  const priceMap = window.__lprPriceMap || {};
  const tier = getFreshTier(answers.weight);
  const trialEntry = priceMap[tier.trialId] ?? priceMap[String(tier.trialId)];
  const portionText = trialEntry?.title || formatGrams(grams);
  document.querySelectorAll('[data-lpr-portion]').forEach(el => {
    el.textContent = portionText;
  });
  document.querySelectorAll('[data-lpr-topping-amount]').forEach(el => {
    el.textContent = formatGrams(toppingGrams);
  });
}

// The pricing row keeps the per-day figure as the headline number — it is the lowest and most
// comparable price on the page — and adds the full amount charged on a second line below it, so
// the total is never a surprise at checkout. The per-day figure is derived FROM the total
// (total ÷ days) rather than the total from a rounded per-day rate: with both numbers on screen,
// per-day × days must land back on the total, otherwise the pair reads as a mistake.
function fillPrices(answers) {
  const priceMap = window.__lprPriceMap || {};
  const trialDays = window.__lprTrialDays || 15;
  const discountPct = window.__lprDiscount ?? 20;
  const tier = getFreshTier(answers.weight);

  const trialEntry = priceMap[tier.trialId] ?? priceMap[String(tier.trialId)];
  if (trialEntry != null) {
    // subscriptionPrice = selling-plan first-order price; price = recurring rate (used as compare-at).
    // The discount code applies to the cart total, so discount the total and divide, not the reverse.
    const fullTotal = trialEntry.subscriptionPrice;
    const discTotal = discountPct > 0 ? discounted(fullTotal, discountPct) : null;
    const fullEl = document.querySelector('[data-lpr-price="trial-full"]');
    const discEl = document.querySelector('[data-lpr-price="trial-disc"]');
    const totalEl = document.querySelector('[data-lpr-total="trial"]');
    if (discTotal !== null) {
      if (fullEl) fullEl.textContent = formatMoney(Math.round(fullTotal / trialDays));
      if (discEl) discEl.textContent = formatMoney(Math.round(discTotal / trialDays));
      if (totalEl) writeTotal(totalEl, discTotal, trialDays);
    } else {
      if (fullEl) fullEl.style.display = 'none';
      if (discEl) discEl.textContent = formatMoney(Math.round(fullTotal / trialDays));
      if (totalEl) writeTotal(totalEl, fullTotal, trialDays);
    }
  } else {
    console.warn('[lpr] trial price not found for variantId:', tier.trialId);
  }

  // The monthly row is hidden for fresh (kibble still renders its own price in it), but the ongoing
  // variant is still resolved for the Klaviyo lp_ongoing_price_per_day prop — keep warning so a
  // recreated product (which changes every variant ID) does not fail silently there too.
  if ((priceMap[tier.ongoingId] ?? priceMap[String(tier.ongoingId)]) == null) {
    console.warn('[lpr] ongoing price not found for variantId:', tier.ongoingId);
  }
}

// Rebuilds the same plan links the normal quiz's buildPlanLink produces, so the Klaviyo event's
// lp_results_url* / lp_checkout_url* properties match (used by discount-escalation email flows).
// Preserves the A/B `variant` so a return visit keeps its tag.
//
// Both links carry the identical ?qa= payload — the only difference is where it lands. The
// results page renders the plan and waits for a click; the checkout page skips straight to the
// cart. Keep them building from one function so a field added to the quiz reaches both.
function buildPlanLink(path, ans, discountOverride) {
  const isK = ans.foodType === 'kibble';
  const discountCode = discountOverride
    || (isK ? '' : (ans.discountCode || localStorage.getItem('longevityDiscountCode') || ''));
  const payload = {
    name: ans.name || '', gender: ans.gender || '', neutered: ans.neutered || '',
    allergies: ans.allergies || [], weight: ans.weight || '', age: ans.age || '',
    birthdate: ans.birthdate || '', isBirthdateUnknown: !!ans.isBirthdateUnknown,
    activity: ans.activity || '', appetite: ans.appetite || '', goal: ans.goal || '',
    email: ans.email || '', foodType: ans.foodType || 'fresh', currentRecipe: ans.currentRecipe || '',
    isCompleted: true, discountCode,
    ...(ans.variant ? { variant: ans.variant } : {}),
    // WEB-334: keep the quiz's id, so a purchase made from the link links to the lead.
    ...(ans.submissionId ? { submissionId: ans.submissionId } : {}),
  };
  const encoded = btoa(String.fromCharCode(...new TextEncoder().encode(JSON.stringify(payload))));
  const url = new URL(path, window.location.origin);
  url.searchParams.set('qa', encoded);
  if (discountCode) url.searchParams.set('discount', discountCode);
  return url.toString();
}

function buildResultsLink(ans, discountOverride) {
  return buildPlanLink(RESULTS_URL, ans, discountOverride);
}

function buildCheckoutLink(ans, discountOverride) {
  return buildPlanLink(CHECKOUT_URL, ans, discountOverride);
}

// The four link properties every Klaviyo write sends, so the profile always carries a plan link
// and a direct-to-checkout link at each discount tier the escalation flows offer.
function buildLinkProps(ans) {
  return {
    lp_results_url: buildResultsLink(ans),
    lp_results_url_10: buildResultsLink(ans, 'LONGEVITY10'),
    lp_results_url_20: buildResultsLink(ans, 'LONGEVITY20'),
    lp_results_url_30: buildResultsLink(ans, 'LONGEVITY30'),
    lp_checkout_url: buildCheckoutLink(ans),
    lp_checkout_url_10: buildCheckoutLink(ans, 'LONGEVITY10'),
    lp_checkout_url_20: buildCheckoutLink(ans, 'LONGEVITY20'),
    lp_checkout_url_30: buildCheckoutLink(ans, 'LONGEVITY30'),
  };
}

// Fire to both dataLayer (for a future GTM container) and gtag directly (the theme's
// live GA4 mechanism — dataLayer.push alone is inert here since there's no GTM).
function track(event, params) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: event, ts: Date.now() }, params));
  } catch (e) {}
  try {
    if (typeof window.gtag === 'function') window.gtag('event', event, params);
  } catch (e) {}
}

async function init() {
  // Set by the direct-to-checkout page (templates/page.longevity_plan_checkout.liquid), which
  // loads this same script so the plan is resolved from one place. Everything up to the Klaviyo
  // side effects runs identically; only the rendering is skipped and the cart is built on load.
  const autoCheckout = !!window.__lprAutoCheckout;
  const qaParam = new URLSearchParams(window.location.search).get('qa');
  if (qaParam) {
    try {
      const decoded = JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(qaParam), c => c.charCodeAt(0))));
      const backfilled = decoded ? backfillAnswers(decoded) : null;
      if (backfilled) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          value: backfilled,
          expiry: Date.now() + 14 * 24 * 60 * 60 * 1000,
        }));
        if (backfilled.discountCode) setDiscountIfBetter(backfilled.discountCode);
      } else {
        // Required field missing — send back to quiz
        window.location.href = QUIZ_URL;
        return;
      }
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('qa');
      history.replaceState(null, '', cleanUrl.toString());
    } catch (e) {
      console.warn('[lpr] failed to decode qa param', e);
    }
  }

  const answers = loadAnswers();

  if (!answers || !answers.isCompleted) {
    window.location.href = QUIZ_URL;
    return;
  }

  // A/B quiz variant carried in via the ?qa= payload; 'default' for the normal flow.
  const variant = answers.variant || 'default';

  // Subscribe to PT Marketing Subscribers list via Klaviyo client API
  if (answers.email) {
    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=TN8fCF', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': '2024-02-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email: answers.email } } },
          },
          relationships: {
            list: { data: { type: 'list', id: 'TPrz5K' } },
          },
        },
      }),
    }).catch(() => {});
  }

  // Detect discount codes applied via /discount/CODE Shopify URLs (3s timeout)
  //
  // Skipped on the checkout page, where this probe is the only thing standing between the click
  // and the redirect and buys nothing: __lprAddToCart runs the same probe itself and recomputes
  // the code it sends, so the cart is charged the same either way. All that changes is the
  // percent reported to GA4 and written as lp_discount_code — and only for a fresh visitor whose
  // browser already holds a cart-applied code better than the one on their own link.
  let cartCode = null;
  if (!autoCheckout) {
    try {
      const cart = await Promise.race([
        fetch('/cart.js').then(r => r.json()),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 3000)),
      ]);
      cartCode = (cart.discount_codes || [])
        .map(d => (d.code || '').toUpperCase())
        .find(c => isKnownDiscountCode(c)) || null;
      if (cartCode) setDiscountIfBetter(cartCode);
    } catch (e) { console.warn('[lpr] cart.js fetch failed/timeout', e); }
  }

  const isKibble = answers.foodType === 'kibble';
  const urlDiscountCode = (new URLSearchParams(window.location.search).get('discount') || '').toUpperCase();
  if (isKnownDiscountCode(urlDiscountCode)) setDiscountIfBetter(urlDiscountCode);

  // getActiveDiscount is safe to call uniformly here: for kibble it only ever consults the
  // current session's own signals (URL param / this quiz completion's answers), never the
  // shared cart/localStorage pool, so a stale fresh-flow code can't leak in and outrank it.
  const discount = getActiveDiscount(cartCode, answers, isKibble);

  if (isKibble && discount.percent <= KIBBLE_LIFETIME_DISCOUNT_PERCENT) {
    // No genuine campaign override this session — clear any stale code from a previous fresh
    // flow so it can't resurface later (e.g. outrank fresh's own default on a future visit).
    localStorage.removeItem('longevityDiscountCode');
    if (answers.discountCode) {
      answers.discountCode = '';
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const item = JSON.parse(raw);
          item.value.discountCode = '';
          localStorage.setItem(STORAGE_KEY, JSON.stringify(item));
        }
      } catch {}
    }
  }
  window.__lprDiscount = discount.percent;
  window.__lprDiscountCode = discount.code;

  track(autoCheckout ? 'direct_checkout_view' : 'results_view', {
    variant: variant,
    food_type: answers.foodType || 'fresh',
    discount_code: discount.code,
    discount_percent: discount.percent,
  });

  // The checkout page carries none of this markup — it redirects before anything could be seen —
  // so it skips straight to the Klaviyo side effects and the cart.
  if (!autoCheckout) {
    if (discount.percent > 0) {
      document.body.classList.add('lpr-discount-active');
      document.querySelectorAll('[data-lpr-discount-badge]').forEach(el => {
        el.textContent = el.classList.contains('lpr-badge')
          ? `-${discount.percent}%`
          : `Desconto ${discount.percent}%`;
        el.removeAttribute('hidden');
      });
    }

    if (isKibble) document.body.classList.add('lpr-kibble');
    applyCopy(answers, isKibble);

    if (isKibble) {
      const trialRow = document.querySelector('[data-lpr-trial-row]');
      if (trialRow) trialRow.style.display = 'none';
      fillKibblePrices(answers);
      fillKibbleCard(answers);
      fillRecipeContent(answers);

    } else {
      // Mirror of the kibble branch above: fresh shows the welcome-pack row only, so the bar stays
      // short on mobile. The row itself has to stay in the markup because kibble fills it.
      const ongoingRow = document.querySelector('[data-lpr-ongoing-row]');
      if (ongoingRow) ongoingRow.style.display = 'none';
      fillPortion(answers);
      fillPrices(answers);
      fillFreshAccordions();
    }
  }

  // Hoisted out of the block below so the Klaviyo event further down can piggyback it, and built
  // whenever there is an email rather than only when the middleware helper happens to be loaded.
  let kProps = null;

  if (answers.email) {
    const priceMap = window.__lprPriceMap || {};
    const trialDays = window.__lprTrialDays || 15;
    const discountPct = window.__lprDiscount ?? 20;
    // The update middleware REPLACES the profile's properties, so send the FULL set the quiz
    // wrote (lp_dog_*, lp_food_type, lp_kibble_recipe, lp_results_url*, lp_checkout_url*) plus
    // the price/bag props added below — otherwise this write wipes the rich props.
    kProps = {
      lp_dog_name: answers.name || '',
      lp_dog_gender: answers.gender || '',
      lp_dog_neutered: answers.neutered || '',
      lp_dog_allergies: (answers.allergies || []).filter(a => a !== 'none').join(', ') || 'none',
      lp_dog_weight_kg: answers.weight || '',
      lp_dog_age: answers.age || '',
      lp_dog_activity: answers.activity || '',
      lp_dog_appetite: answers.appetite || '',
      lp_dog_goal: answers.goal || '',
      lp_food_type: answers.foodType || 'fresh',
      lp_kibble_recipe: answers.currentRecipe || '',
      lp_quiz_completed_at: new Date().toISOString(),
      lp_discount_code: discount.code,
      lp_quiz_variant: variant,
      ...buildLinkProps(answers),
    };
    if (isKibble) {
      kProps.lp_kibble_bag_weight = getKibbleBagWeight(answers.weight);
      const kibbleEntry = getKibbleVariant(answers);
      if (kibbleEntry) {
        // Same derivation as fillKibblePrices (discount the total, then divide) so the email and
        // the results page never show per-day figures a cent apart.
        const fullTotal = kibbleEntry.subscriptionPrice;
        const total = discountPct > 0 ? discounted(fullTotal, discountPct) : fullTotal;
        kProps.lp_ongoing_price_per_day = (Math.round(fullTotal / KIBBLE_BAG_DAYS) / 100).toFixed(2);
        kProps.lp_discounted_price_per_day = (Math.round(total / KIBBLE_BAG_DAYS) / 100).toFixed(2);
      }
    } else {
      const tier = getFreshTier(answers.weight);
      const trialEntry = priceMap[tier.trialId] ?? priceMap[String(tier.trialId)];
      const ongoingEntry = priceMap[tier.ongoingId] ?? priceMap[String(tier.ongoingId)];
      kProps.lp_daily_grams = parseInt(trialEntry?.title) || calcPortionGrams(answers.weight);
      if (trialEntry) {
        // Same derivation as fillPrices (discount the total, then divide) so the email and the
        // results page never show per-day figures a cent apart.
        const fullTotal = trialEntry.subscriptionPrice;
        const total = discountPct > 0 ? discounted(fullTotal, discountPct) : fullTotal;
        kProps.lp_discounted_price_per_day = (Math.round(total / trialDays) / 100).toFixed(2);
      }
      if (ongoingEntry) {
        kProps.lp_ongoing_price_per_day = (Math.round(ongoingEntry.price / 30) / 100).toFixed(2);
      }
    }
    // Silently no-ops when the profile does not exist yet — which is every lead arriving straight
    // from an A/B quiz, because nothing on this page creates the profile. The event below is what
    // actually covers those.
    if (typeof window.updateKlaviyoProfile === 'function') {
      window.updateKlaviyoProfile(answers.email, kProps).catch(() => {});
    }
  }

  // New-quiz completion side effects — parity with the normal quiz's syncKlaviyo. Gated so they run
  // once per fresh new-quiz completion: `variant` => new quiz only (the normal quiz does these itself
  // and has no variant); `qaParam` => fresh arrival from the quiz, never a results-page refresh.
  if (qaParam && answers.variant && answers.email) {
    // GA4 "lead" + Google Ads conversion, with acquisition/UTM captured globally by bk-utm-tracker.js
    try {
      if (typeof window.gtag === 'function') {
        let bkFirst = {}, bkLast = {}, bkCoupon = '';
        try { bkFirst = JSON.parse(localStorage.getItem('bk_utm_first') || 'null') || {}; } catch (e) {}
        try { bkLast = JSON.parse(localStorage.getItem('bk_utm_last') || 'null') || {}; } catch (e) {}
        try { bkCoupon = JSON.parse(localStorage.getItem('bk_coupon') || 'null') || ''; } catch (e) {}
        const leadPayload = {
          email: answers.email,
          variant: variant,
          acquisition_first_source: bkFirst.source || '',
          acquisition_first_medium: bkFirst.medium || '',
          acquisition_first_campaign: bkFirst.campaign || '',
          acquisition_first_content: bkFirst.content || '',
          acquisition_first_term: bkFirst.term || '',
          acquisition_last_source: bkLast.source || '',
          acquisition_last_medium: bkLast.medium || '',
          acquisition_last_campaign: bkLast.campaign || '',
          acquisition_last_content: bkLast.content || '',
          acquisition_last_term: bkLast.term || '',
          first_touch_match_type: bkFirst.match_type || 'direct',
          coupon_applied: bkCoupon,
        };
        window.gtag('set', 'user_properties', leadPayload);
        window.gtag('event', 'lead', leadPayload);
      }
    } catch (e) { console.warn('[lpr] lead event failed', e); }

    // Account claim (backoffice) — same endpoint/payload as the normal quiz
    try {
      const authApiUrl = document.querySelector('.barkyn-auth-api-url')?.value
        || 'https://8j0t014re0.execute-api.eu-west-3.amazonaws.com/dev';
      fetch(`${authApiUrl}/klaviyo-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: answers.email, claimedAccount: true, isLongevity: true }),
      }).catch(() => {});
    } catch (e) {}
  }

  // Anyone arriving on a ?qa= link may never have run the quiz — a plan link sent by support or
  // the WhatsApp agent, a forwarded URL, a QA link. Those leads never had the quiz-completed
  // event fired for them, and the updateKlaviyoProfile PATCH above no-ops while their profile
  // does not exist yet, so they ended up with a bare profile. Firing the event here lands both
  // the event and the properties: /client/events upserts the profile atomically.
  //
  // Deliberately OUTSIDE the variant gate above, unlike the GA4 lead and the account claim,
  // which stay in it: `variant` is only ever present on an A/B or QA link, exactly the
  // population that did not need the help. Those two would count existing customers as new
  // leads and claim accounts for addresses that never asked.
  //
  // Trade-off: a lead who did run the quiz and later clicks the lp_results_url in their email
  // fires the metric a second time. Harmless if the flow runs once per profile; `unique_id` on
  // both this event and the quiz's is the fix if it does not.
  if (qaParam && answers.email) {
    fetch('https://a.klaviyo.com/client/events/?company_id=TN8fCF', {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', 'revision': '2024-02-15' },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            properties: {
              lp_dog_name: answers.name || '',
              lp_dog_gender: answers.gender || '',
              lp_dog_neutered: answers.neutered || '',
              lp_dog_allergies: (answers.allergies || []).filter(a => a !== 'none').join(', ') || 'none',
              lp_dog_weight_kg: answers.weight || '',
              lp_dog_age: answers.age || '',
              lp_dog_activity: answers.activity || '',
              lp_dog_appetite: answers.appetite || '',
              lp_dog_goal: answers.goal || '',
              lp_food_type: answers.foodType || 'fresh',
              lp_kibble_recipe: answers.currentRecipe || '',
              lp_quiz_completed_at: new Date().toISOString(),
              lp_discount_code: discount.code || '',
              ...buildLinkProps(answers),
              variant: variant,
            },
            metric: {
              data: {
                type: 'metric',
                attributes: { name: isKibble ? 'LP Quiz Completed - Kibble' : 'LP Quiz Completed - Fresh' },
              },
            },
            // Piggyback the profile properties, same as the normal quiz does: /client/events
            // upserts the profile atomically, so the lp_* props land for a brand-new lead too.
            // Without this the profile was created bare and stayed bare until a second visit made
            // the updateKlaviyoProfile call above find something to patch.
            profile: {
              data: {
                type: 'profile',
                attributes: { email: answers.email, ...(kProps ? { properties: kProps } : {}) },
              },
            },
          },
        },
      }),
    }).catch(() => {});
  }

  // Direct-to-checkout link (Klaviyo emails): the plan is already resolved above, so build the
  // cart and go. Nothing on this page is clickable — the visitor only ever sees the loader.
  if (autoCheckout) {
    // The fresh recipe is chicken-based, so the quiz blocks these leads and never writes a
    // checkout link for them (see hasBlockingAllergy in longevity_plan_quiz.js). A forwarded or
    // hand-edited ?qa= can still land here — skipping the plan screen would put the dog straight
    // into a checkout for the one thing it is allergic to, so send it to the results page
    // instead, where the plan is at least visible before anyone pays.
    if (answers.foodType !== 'kibble' && (answers.allergies || []).includes('chicken')) {
      console.warn('[lpr] blocking allergy on a checkout link — showing the plan instead');
      window.location.href = buildResultsLink(answers);
      return;
    }

    track('direct_checkout_start', {
      variant: variant,
      food_type: answers.foodType || 'fresh',
      discount_code: discount.code,
      discount_percent: discount.percent,
    });
    try {
      await window.__lprAddToCart(answers);
    } catch (err) {
      // Never leave a dead screen on a link someone opened from an email. The results page
      // rebuilds the same plan from the same answers, and its CTA has its own AJAX-cart
      // fallback, so the visitor still has a way through.
      console.error('[lpr] auto checkout failed, falling back to the results page:', err);
      window.location.href = buildResultsLink(answers);
    }
    return;
  }

  const ctaBtn = document.getElementById('lpr-cta-btn');
  if (ctaBtn) {
    function handleCTA(e) {
      e.preventDefault();
      if (ctaBtn.disabled) return;
      track('checkout_click', { variant: variant, food_type: answers.foodType || 'fresh' });
      ctaBtn.disabled = true;
      ctaBtn.textContent = 'A adicionar ao carrinho...';
      window.__lprAddToCart(answers).catch((err) => {
        console.error('[lpr] addToCart error:', err);
        ctaBtn.disabled = false;
        ctaBtn.textContent = 'Continuar';
      });
    }
    ctaBtn.addEventListener('click', handleCTA);
    ctaBtn.addEventListener('touchend', handleCTA);
  }

  window.addEventListener('pageshow', (e) => {
    if (e.persisted && ctaBtn) {
      ctaBtn.disabled = false;
      ctaBtn.textContent = 'Continuar';
    }
  });
}

async function navigateToCheckout(url) {
  try {
    const resolved = await fetch(url, { redirect: 'follow', credentials: 'include' });
    if (resolved && resolved.url && resolved.url !== url) {
      window.location.href = resolved.url;
      return;
    }
  } catch (e) {
    console.warn('[lpr] checkout URL resolve failed:', e);
  }
  window.location.href = url;
}

async function setCartBuyerEmail(email) {
  const diag = { at: new Date().toISOString(), apiVersion: STOREFRONT_API_VERSION };
  try {
    if (!email) { diag.skipped = 'no email'; return; }
    const cart = await fetch('/cart.js').then(r => r.json());
    const token = cart && cart.token;
    diag.hasCartToken = !!token;
    if (!token) return;
    const shopDomain = window.Shopify?.shopDomain || window.location.hostname;
    diag.shopDomain = shopDomain;
    const sfHttp = await fetch(`https://${shopDomain}/api/${STOREFRONT_API_VERSION}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': '840e61d28bd281caf981da59f6faecb9',
      },
      body: JSON.stringify({
        query: `mutation cartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
          cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
            cart { buyerIdentity { email } }
            userErrors { field message }
          }
        }`,
        variables: {
          cartId: `gid://shopify/Cart/${token}`,
          buyerIdentity: { email: email },
        },
      }),
    });
    const sfRes = await sfHttp.json().catch(() => null);
    diag.httpStatus = sfHttp.status;
    diag.graphqlErrors = sfRes?.errors || null;
    diag.userErrors = sfRes?.data?.cartBuyerIdentityUpdate?.userErrors || null;
    diag.emailSetOnCart = !!sfRes?.data?.cartBuyerIdentityUpdate?.cart?.buyerIdentity?.email;
  } catch (e) {
    diag.exception = String((e && e.message) || e);
  } finally {
    try { localStorage.setItem('lpr_buyeridentity_debug', JSON.stringify(diag)); } catch (e2) {}
  }
}

window.__lprAddToCart = async function (answers) {
  const isKibble = answers.foodType === 'kibble';
  const quizVariant = answers.variant || '';
  let variantId, sellingPlan;

  if (isKibble) {
    const kibbleEntry = getKibbleVariant(answers);
    if (!kibbleEntry) throw new Error('kibble variant not found');
    variantId = kibbleEntry.id;
    sellingPlan = KIBBLE_SELLING_PLAN;
  } else {
    const tier = getFreshTier(answers.weight);
    const priceMap = window.__lprPriceMap || {};
    const trialEntry = priceMap[tier.trialId] ?? priceMap[String(tier.trialId)];
    variantId = tier.trialId;
    sellingPlan = trialEntry?.sellingPlanId ?? LOOP_SELLING_PLAN;
  }


  const dogObject = buildDogPayload(answers);
  const dogObjectKlaviyo = [{
    dog1_name: answers.name || '',
    dog1_gender: answers.gender || '',
    dog1_lifestage: answers.age || '',
    dog1_breed: 'unknown',
    dog1_weight: 'unknown',
    dog1_monthly_food_amount: `${answers.weight} kg`,
    dog1_flavour_preference: (answers.allergies || []).includes('chicken') ? 'hypoallergenic' : 'chicken',
    dog1_feeding_habits: '',
    dog1_improve: answers.goal || '',
  }];

  // Detect session discount before clearing cart
  let freshCartCode = null;
  try {
    const existingCart = await fetch('/cart.js').then(r => r.json());
    freshCartCode = (existingCart.discount_codes || [])
      .map(d => (d.code || '').toUpperCase())
      .find(c => isKnownDiscountCode(c)) || null;
    if (freshCartCode) setDiscountIfBetter(freshCartCode);
  } catch (e) {}
  const { code } = getActiveDiscount(freshCartCode, answers, isKibble);

  const items = [
    {
      id: variantId,
      quantity: 1,
      selling_plan: sellingPlan,
      properties: Object.assign({
        _dogs: JSON.stringify([dogObject]),
        _dogs_klaviyo: JSON.stringify(dogObjectKlaviyo),
        _subscriptionType: 'monthlySubscription_v1',
      }, quizVariant ? { _quiz_variant: quizVariant } : {}),
    },
    ...TOPPING_VARIANTS,
  ];
  if (isKibble) {
    document.cookie = 'cart=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  }
  await fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  });
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    throw new Error((errBody?.description || errBody?.message || 'cart add failed') + ' [' + res.status + ']');
  }
  await setCartBuyerEmail(answers.email);
  await navigateToCheckout((window.__lprDiscount ?? 0) > 0
    ? `/checkout?discount=${encodeURIComponent(code)}`
    : '/checkout');
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
